import React from 'react';

interface InvoiceDetail {
  id: number;
  numero: string;
  date_emission: string;
  patient: {
    nom: string;
    prenoms: string;
    numero_assure: string;
    nom_souscripteur: string;
  };
  assureur: {
    nom: string;
    contact: string;
    telephone: string;
    email: string;
    adresse: string;
    taux_couverture: number;
  };
  montant_total: number;
  montant_assurance: number;
  montant_patient: number;
  pourcentage_assurance: number;
  lignes: Array<{
    treatment: string;
    quantite: number;
    prix_unitaire: number;
    montant: number;
  }>;
}

interface DetailedInvoiceTemplateProps {
  invoice: InvoiceDetail;
}

const DetailedInvoiceTemplate = React.forwardRef<HTMLDivElement, DetailedInvoiceTemplateProps>(
  ({ invoice }, ref) => {
    return (
      <div ref={ref} className="detailed-print-container bg-white">
        <style>{`
          @media print {
            @page {
              size: A4;
              margin: 10mm;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .detailed-print-container {
              width: 100%;
              max-width: 210mm;
              margin: 0 auto;
            }
            .no-print {
              display: none !important;
            }
          }
          .detailed-print-container {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #1a1a1a;
            max-width: 210mm;
            margin: 0 auto;
            padding: 10px;
            font-size: 11px;
          }
          .invoice-header {
            border-bottom: 3px solid #2563eb;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          .invoice-logo {
            max-height: 60px;
            max-width: 180px;
          }
          .invoice-number {
            font-size: 22px;
            font-weight: bold;
            color: #2563eb;
          }
          .info-section {
            background: #f8fafc;
            border-left: 3px solid #2563eb;
            padding: 8px;
            margin-bottom: 10px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
          }
          .items-table th {
            background: #1e40af;
            color: white;
            padding: 8px 6px;
            text-align: left;
            font-size: 11px;
            font-weight: 600;
            border: 1px solid #1e3a8a;
          }
          .items-table td {
            padding: 6px;
            border: 1px solid #e2e8f0;
            font-size: 10px;
          }
          .items-table tbody tr:nth-child(even) {
            background: #f8fafc;
          }
          .totals-section {
            margin-top: 12px;
            border-top: 2px solid #2563eb;
            padding-top: 10px;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 11px;
          }
          .final-total {
            background: #dbeafe;
            border: 2px solid #2563eb;
            border-radius: 6px;
            padding: 8px;
            margin-top: 6px;
          }
          .notes-section {
            background: #fef3c7;
            border-left: 3px solid #f59e0b;
            padding: 8px;
            margin: 10px 0;
            font-size: 10px;
          }
          .footer-section {
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px solid #e2e8f0;
          }
          .empty-message {
            text-align: center;
            padding: 20px;
            color: #dc2626;
            font-weight: 600;
            background: #fee2e2;
            border: 1px solid #dc2626;
            border-radius: 6px;
            margin: 10px 0;
          }
        `}</style>

        {/* Header */}
        <div className="invoice-header">
          <div className="flex justify-between items-start mb-2">
            <div>
              <img src="/Logo - SONRISA.png" alt="Cabinet SONRISA" className="invoice-logo mb-2" />
              <div className="text-[10px] text-gray-700">
                <div className="font-semibold text-sm text-gray-900 mb-1">Cabinet Dentaire SONRISA</div>
                <div>Conakry, Guinée | Tél: +224 620 46 63 29</div>
                <div>contact@cabinet-sonrisa.clinic | www.cabinet-sonrisa.clinic</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-600 mb-1">FACTURE ASSURANCE</div>
              <div className="invoice-number">{invoice.numero}</div>
              <div className="text-xs mt-1">
                <div>Date: {new Date(invoice.date_emission).toLocaleDateString('fr-FR')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Patient and Insurance Information */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Patient Info */}
          <div className="info-section">
            <div className="font-bold text-xs text-gray-700 mb-2">INFORMATIONS PATIENT</div>
            <div className="space-y-0.5 text-[10px]">
              <div><span className="font-semibold">Nom:</span> {invoice.patient.nom} {invoice.patient.prenoms}</div>
              <div><span className="font-semibold">N° Assuré:</span> {invoice.patient.numero_assure}</div>
              {invoice.patient.nom_souscripteur && (
                <div><span className="font-semibold">Souscripteur:</span> {invoice.patient.nom_souscripteur}</div>
              )}
            </div>
          </div>

          {/* Insurance Info */}
          <div className="info-section">
            <div className="font-bold text-xs text-gray-700 mb-2">INFORMATIONS ASSURANCE</div>
            <div className="space-y-0.5 text-[10px]">
              <div><span className="font-semibold">Compagnie:</span> {invoice.assureur.nom}</div>
              <div><span className="font-semibold">Contact:</span> {invoice.assureur.contact}</div>
              <div><span className="font-semibold">Tél:</span> {invoice.assureur.telephone}</div>
              {invoice.assureur.email && (
                <div><span className="font-semibold">Email:</span> {invoice.assureur.email}</div>
              )}
              <div><span className="font-semibold">Taux:</span> {invoice.assureur.taux_couverture}%</div>
            </div>
          </div>
        </div>

        {/* Services/Treatments Table */}
        <div>
          <h3 className="text-xs font-bold text-gray-800 mb-2">DÉTAILS DES ACTES MÉDICAUX</h3>
          {invoice.lignes && invoice.lignes.length > 0 ? (
            <table className="items-table">
              <thead>
                <tr>
                  <th className="w-8 text-center">#</th>
                  <th>Description de l'acte</th>
                  <th className="w-16 text-center">Qté</th>
                  <th className="w-24 text-right">Prix Unit.</th>
                  <th className="w-24 text-right">Montant</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lignes.map((ligne, index) => (
                  <tr key={index}>
                    <td className="text-center text-gray-600">{index + 1}</td>
                    <td className="font-medium">{ligne.treatment}</td>
                    <td className="text-center">{ligne.quantite}</td>
                    <td className="text-right">{ligne.prix_unitaire.toLocaleString()} GNF</td>
                    <td className="text-right font-semibold">{ligne.montant.toLocaleString()} GNF</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-message">
              ⚠️ Aucun acte médical enregistré pour cette facture
            </div>
          )}
        </div>

        {/* Totals Section */}
        <div className="totals-section">
          <div className="max-w-md ml-auto">
            <div className="total-row">
              <span className="font-semibold">Montant Total HT:</span>
              <span className="font-semibold">{invoice.montant_total.toLocaleString()} GNF</span>
            </div>
            <div className="total-row text-gray-600">
              <span>Taux de prise en charge:</span>
              <span>{invoice.pourcentage_assurance || invoice.assureur.taux_couverture}%</span>
            </div>
            <div className="total-row">
              <span className="font-semibold">Part Assurance:</span>
              <span className="font-semibold text-blue-700">{invoice.montant_assurance.toLocaleString()} GNF</span>
            </div>
            <div className="total-row">
              <span className="font-semibold">Part Patient:</span>
              <span className="font-semibold text-green-700">{invoice.montant_patient.toLocaleString()} GNF</span>
            </div>

            <div className="final-total">
              <div className="flex justify-between items-center">
                <span className="text-sm font-bold">MONTANT À RÉGLER PAR L'ASSURANCE:</span>
                <span className="text-lg font-bold text-blue-700">{invoice.montant_assurance.toLocaleString()} GNF</span>
              </div>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="notes-section">
          <div className="font-bold text-[10px] mb-1">⚠️ INFORMATIONS IMPORTANTES</div>
          <ul className="list-disc list-inside space-y-0.5 text-[9px]">
            <li>Facture conforme aux conventions Cabinet SONRISA / {invoice.assureur.nom}</li>
            <li>Règlement sous 30 jours à compter de la date d'émission</li>
            <li>Contact: +224 620 46 63 29 | contact@cabinet-sonrisa.clinic</li>
          </ul>
        </div>

        {/* Signature Section */}
        <div className="footer-section">
          <div className="grid grid-cols-2 gap-8 mb-4">
            <div>
              <div className="font-semibold mb-8 text-xs">Pour le Cabinet SONRISA</div>
              <div className="border-t border-gray-400 pt-1">
                <div className="text-[9px] text-gray-600">Signature et cachet</div>
              </div>
            </div>
            <div>
              <div className="font-semibold mb-8 text-xs">Pour {invoice.assureur.nom}</div>
              <div className="border-t border-gray-400 pt-1">
                <div className="text-[9px] text-gray-600">Signature et cachet</div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-[9px] text-gray-500 mt-3 pt-2 border-t border-gray-300">
            <div>
              Document généré le {new Date().toLocaleDateString('fr-FR')} | Cabinet SONRISA - Conakry, Guinée
            </div>
          </div>
        </div>
      </div>
    );
  }
);

DetailedInvoiceTemplate.displayName = 'DetailedInvoiceTemplate';

export default DetailedInvoiceTemplate;
